import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    Box,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Autocomplete,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import pricebookAPI from 'services/pricebook/api';
import { gatherCatalog } from 'services/utils/enums';

const RulesTable = ({ pricebook, displayPricebook, errorContext, onRulesUpdated }) => {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [catalog, setCatalog] = useState(null);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [docsOpen, setDocsOpen] = useState(false);
    const [docsContent, setDocsContent] = useState('');
    const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
    const [conflictData, setConflictData] = useState(null);
    /* eslint-disable camelcase */
    const [newRule, setNewRule] = useState({
        service_type: 'Keys',
        repair_type: '',
        part: '',
        position: '',
        price: '',
        year_start: '',
        year_end: '',
        open_ended: false,
        make: [],
        model: [],
        includes_future_models: false,
    });
    const [requiresQuote, setRequiresQuote] = useState(false);
    const [vehicleMakes, setVehicleMakes] = useState([]);
    const [vehicleModels, setVehicleModels] = useState([]);
    const [allVehicleModels, setAllVehicleModels] = useState([]);
    /* eslint-enable camelcase */

    useEffect(() => {
        // Load catalog once for service_detail_id lookups
        if (!catalog) {
            gatherCatalog().then((response) => {
                const catalogData = response?.data?.payload || [];
                console.log('Catalog loaded in RulesTable:', catalogData);
                setCatalog(catalogData);
            }).catch(err => {
                console.error('Error loading catalog:', err);
            });
        }
    }, []);

    useEffect(() => {
        // Fetch vehicle makes and models for autocomplete
        const fetchVehicleData = async () => {
            try {
                const [makesResponse, modelsResponse] = await Promise.all([
                    pricebookAPI.getVehicleMakes(),
                    pricebookAPI.getVehicleModels(),
                ]);

                if (makesResponse?.data?.payload) {
                    setVehicleMakes(makesResponse.data.payload);
                }

                if (modelsResponse?.data?.payload) {
                    setVehicleModels(modelsResponse.data.payload);
                    setAllVehicleModels(modelsResponse.data.payload);
                }
            } catch (error) {
                console.error('Error fetching vehicle data:', error);
                errorContext.DISPATCH({
                    type: 'API_ERROR',
                    payload: { alert: 'Failed to load vehicle makes/models' },
                });
            }
        };

        fetchVehicleData();
    }, []);

    // Filter models by selected makes in Add Rule modal (debounced)
    useEffect(() => {
        const timer = setTimeout(async () => {
            try {
                if (newRule.make && Array.isArray(newRule.make) && newRule.make.length > 0) {
                    const resp = await pricebookAPI.getVehicleModels(newRule.make);
                    if (resp?.data?.payload) {
                        const models = resp.data.payload;
                        setVehicleModels(models);
                        // Trim selected models to those available
                        if (Array.isArray(newRule.model) && newRule.model.length > 0) {
                            const trimmed = newRule.model.filter(m => models.includes(m));
                            if (trimmed.length !== newRule.model.length) {
                                setNewRule({ ...newRule, model: trimmed });
                            }
                        }
                    }
                } else {
                    setVehicleModels(allVehicleModels);
                }
            } catch (e) {
                console.error('Error filtering models by make:', e);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [newRule.make, allVehicleModels, newRule.model]);

    useEffect(() => {
        // Prefer rules from search results when provided via displayPricebook
        if (displayPricebook?.rules) {
            console.log('Using rules from search response (displayPricebook):', displayPricebook.rules);
            setRules(displayPricebook.rules);
            setLoading(false);
        } else if (displayPricebook === null) {
            // Explicitly clear rules when displayPricebook is null (no search results)
            console.log('No search results - clearing displayed rules');
            setRules([]);
            setLoading(false);
        } else if (pricebook?.rules) {
            console.log('Using rules from pricebook prop:', pricebook.rules);
            setRules(pricebook.rules);
            setLoading(false);
        } else {
            // Otherwise fetch rules from the selected pricebook id (for mutations)
            loadRules();
        }
    }, [displayPricebook, pricebook?.id, pricebook?.rules]);

    const getServiceDetailInfo = (serviceDetailId) => {
        if (!catalog || !Array.isArray(catalog) || catalog.length === 0) {
            console.log('Catalog not loaded yet or empty');
            /* eslint-disable-next-line camelcase */
            return { service_type: '-', repair_type: '-', part: '-', position: '-' };
        }

        const detail = catalog.find(item => item.id === serviceDetailId);
        console.log(`Looking up service_detail_id ${serviceDetailId}:`, detail);

        if (!detail) {
            console.log(`No catalog entry found for service_detail_id ${serviceDetailId}`);
            /* eslint-disable-next-line camelcase */
            return { service_type: '-', repair_type: '-', part: '-', position: '-' };
        }

        /* eslint-disable camelcase */
        return {
            service_type: detail.service_type || '-',
            repair_type: detail.repair_type || '-',
            part: detail.part || '-',
            position: detail.position || '-',
        };
        /* eslint-enable camelcase */
    };

    // Sort and group rules by repair_type, part, position, then rank
    const sortedAndGroupedRules = useMemo(() => {
        if (!rules || rules.length === 0) return [];
        if (!catalog || !Array.isArray(catalog)) return rules;

        // Enrich rules with service detail info for sorting
        const enrichedRules = rules.map(rule => {
            const serviceInfo = getServiceDetailInfo(rule.service_detail_id);
            return {
                ...rule,
                _serviceInfo: serviceInfo,
            };
        });

        // Sort by: repair_type -> part -> position -> rank (descending)
        return enrichedRules.sort((a, b) => {
            // Group by repair_type first
            if (a._serviceInfo.repair_type !== b._serviceInfo.repair_type) {
                return (a._serviceInfo.repair_type || '').localeCompare(b._serviceInfo.repair_type || '');
            }
            // Then by part
            if (a._serviceInfo.part !== b._serviceInfo.part) {
                return (a._serviceInfo.part || '').localeCompare(b._serviceInfo.part || '');
            }
            // Then by position
            if (a._serviceInfo.position !== b._serviceInfo.position) {
                return (a._serviceInfo.position || '').localeCompare(b._serviceInfo.position || '');
            }
            // Finally by rank (highest first)
            return (b.rank || 0) - (a.rank || 0);
        });
    }, [rules, catalog]);

    const formatYearRange = (yearRange) => {
        if (!yearRange) return 'All Years';
        const { start, end } = yearRange;
        if (!start && !end) return 'All Years';

        // Treat 9999 as infinity
        const displayStart = start && start < 9999 ? start : null;
        const displayEnd = end && end < 9999 ? end : null;

        // If both are effectively infinity, show "All Years"
        if (!displayStart && !displayEnd) return 'All Years';

        // If they're the same specific year
        if (displayStart === displayEnd) return displayStart;

        // Show range with infinity symbol for open-ended values
        return `${displayStart || '∞'} - ${displayEnd || '∞'}`;
    };

    const formatArray = (arr, defaultText = 'All') => {
        if (!arr || !Array.isArray(arr) || arr.length === 0) return defaultText;
        return arr.join(', ');
    };

    // Catalog-based dropdown options for add rule modal
    const serviceTypeOptions = useMemo(() => {
        if (!catalog || !Array.isArray(catalog)) return [];
        const arr = catalog.map(item => item.service_type).filter(Boolean);
        return arr.filter((v, i, a) => a.indexOf(v) === i).sort();
    }, [catalog]);

    const repairTypeOptions = useMemo(() => {
        if (!catalog || !Array.isArray(catalog)) return [];
        const filtered = newRule.service_type
            ? catalog.filter(item => item.service_type === newRule.service_type)
            : catalog;
        const arr = filtered.map(item => item.repair_type).filter(Boolean);
        return arr.filter((v, i, a) => a.indexOf(v) === i).sort();
    }, [catalog, newRule.service_type]);

    const partOptions = useMemo(() => {
        if (!catalog || !Array.isArray(catalog)) return [];
        let filtered = catalog;
        if (newRule.service_type) {
            filtered = filtered.filter(item => item.service_type === newRule.service_type);
        }
        if (newRule.repair_type) {
            filtered = filtered.filter(item => item.repair_type === newRule.repair_type);
        }
        const arr = filtered.map(item => item.part).filter(Boolean);
        return arr.filter((v, i, a) => a.indexOf(v) === i).sort();
    }, [catalog, newRule.service_type, newRule.repair_type]);

    const positionOptions = useMemo(() => {
        if (!catalog || !Array.isArray(catalog)) return [];
        let filtered = catalog;

        if (newRule.service_type) {
            filtered = filtered.filter(item => item.service_type === newRule.service_type);
        }
        if (newRule.repair_type) {
            filtered = filtered.filter(item => item.repair_type === newRule.repair_type);
        }
        if (newRule.part) {
            filtered = filtered.filter(item => item.part === newRule.part);
        }

        const arr = filtered.map(item => item.position).filter(Boolean);
        const uniquePositions = arr.filter((v, i, a) => a.indexOf(v) === i).sort();
        return uniquePositions;
    }, [catalog, newRule.service_type, newRule.repair_type, newRule.part]);

    // Find service_detail_id from catalog based on service_type + repair_type + part + position
    const findServiceDetailId = (serviceType, repairType, part, position) => {
        if (!catalog || !Array.isArray(catalog)) return null;

        // Normalize position: empty string or null means "no position"
        const normalizedPosition = position || null;

        const match = catalog.find(item =>
            item.service_type === serviceType &&
            item.repair_type === repairType &&
            item.part === part &&
            (normalizedPosition === null ? !item.position : item.position === normalizedPosition));

        return match?.id || null;
    };

    const loadRules = async () => {
        if (!pricebook?.id) return;

        setLoading(true);
        try {
            const response = await pricebookAPI.getPricebookRules(pricebook.id);
            if (response?.data?.status === 200) {
                setRules(response.data.payload || []);
            }
        } catch (error) {
            errorContext.DISPATCH({
                type: 'API_ERROR',
                payload: { alert: 'Error loading rules' },
            });
        } finally {
            setLoading(false);
        }
    };

    const loadDocs = async () => {
        try {
            const response = await pricebookAPI.getDocumentation();
            if (response?.data?.status === 200 && response?.data?.payload) {
                setDocsContent(response.data.payload);
                setDocsOpen(true);
            }
        } catch (error) {
            errorContext.DISPATCH({
                type: 'API_ERROR',
                payload: { alert: 'Error loading documentation' },
            });
        }
    };

    const handleDeleteRule = async (ruleId) => {
        if (!window.confirm('Are you sure you want to delete this rule?')) {
            return;
        }

        try {
            const response = await pricebookAPI.deletePricebookRule(pricebook.id, ruleId);
            if (response?.data?.status === 200) {
                errorContext.DISPATCH({
                    type: 'NO_ERROR',
                    payload: { alert: 'Rule deleted successfully' },
                });
                loadRules();
                if (onRulesUpdated) onRulesUpdated();
            }
        } catch (error) {
            errorContext.DISPATCH({
                type: 'API_ERROR',
                payload: { alert: 'Error deleting rule' },
            });
        }
    };

    const handleAddRule = async (force = false) => {
        // Validate required fields (price not required if requiresQuote is true)
        if (!newRule.service_type || !newRule.repair_type || !newRule.part) {
            errorContext.DISPATCH({
                type: 'INVALID_INPUT',
                payload: { alert: 'Service Type, Repair Type, and Part are required' },
            });
            return;
        }

        if (!requiresQuote && !newRule.price) {
            errorContext.DISPATCH({
                type: 'INVALID_INPUT',
                payload: { alert: 'Price is required (or check "Manual Quote Required")' },
            });
            return;
        }

        // Find service_detail_id
        const serviceDetailId = findServiceDetailId(newRule.service_type, newRule.repair_type, newRule.part, newRule.position);
        if (!serviceDetailId) {
            errorContext.DISPATCH({
                type: 'INVALID_INPUT',
                payload: { alert: 'Invalid combination of Service Type, Repair Type, Part, and Position' },
            });
            return;
        }

        try {
            // Build year_range object if either year is provided
            let yearRange = undefined;
            if (newRule.year_start || newRule.year_end || newRule.open_ended) {
                const startYear = newRule.year_start ? parseInt(newRule.year_start) : undefined;
                const endYear = newRule.open_ended ? 9999 : (newRule.year_end ? parseInt(newRule.year_end) : undefined);
                
                // Validate year range: end must be >= start
                // Equal years (start == end) is valid and represents a single year
                if (startYear && endYear && endYear < 9999 && endYear < startYear) {
                    errorContext.DISPATCH({
                        type: 'INVALID_INPUT',
                        payload: { alert: `End year (${endYear}) cannot be less than start year (${startYear})` },
                    });
                    return;
                }
                
                yearRange = {
                    start: startYear,
                    end: endYear,
                };
            }

            /* eslint-disable camelcase */
            const ruleData = {
                rules: [
                    {
                        service_detail_id: serviceDetailId,
                        price: requiresQuote ? null : parseFloat(newRule.price),
                        year_range: yearRange,
                        make: (newRule.make && newRule.make.length > 0) ? newRule.make : undefined,
                        model: (newRule.model && newRule.model.length > 0) ? newRule.model : undefined,
                        includes_future_models: newRule.includes_future_models || undefined,
                    },
                ],
                force: force || undefined,
            };
            /* eslint-enable camelcase */

            console.log('Adding rule to pricebook:', pricebook);
            console.log('Using pricebook ID:', pricebook.id);
            console.log('Rule data:', ruleData);

            const response = await pricebookAPI.addPricebookRule(pricebook.id, ruleData);
            if (response?.data?.status === 200 || response?.data?.status === 201) {
                errorContext.DISPATCH({
                    type: 'NO_ERROR',
                    payload: { alert: 'Rule added successfully' },
                });
                setAddDialogOpen(false);
                setConflictDialogOpen(false);
                setConflictData(null);
                setRequiresQuote(false);
                /* eslint-disable camelcase */
                setNewRule({
                    service_type: 'Keys',
                    repair_type: '',
                    part: '',
                    position: '',
                    price: '',
                    year_start: '',
                    year_end: '',
                    open_ended: false,
                    make: [],
                    model: [],
                    includes_future_models: false,
                });
                /* eslint-enable camelcase */
                loadRules();
                if (onRulesUpdated) onRulesUpdated();
            }
        } catch (error) {
            console.error('Error adding rule:', error);
            console.error('Error response:', error.response);
            // Check for conflict (409)
            if (error.response?.data?.status === 409) {
                console.log('Conflict detected:', error.response.data);
                setConflictData(error.response.data);
                setConflictDialogOpen(true);
            } else {
                errorContext.DISPATCH({
                    type: 'API_ERROR',
                    payload: { alert: error.response?.data?.message || error.message || 'Error adding rule' },
                });
            }
        }
    };

    const handleForceAddRule = () => {
        handleAddRule(true);
    };

    return (
        <Card sx={{ mt: 3 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                        <Typography variant='h6'>
                            Pricebook Rules
                        </Typography>
                        {(displayPricebook || pricebook) && (
                            <Typography variant='body2' color='text.secondary'>
                                {(() => {
                                    const companyName = displayPricebook?.company_name || pricebook?.company_name;
                                    const pricebookName = displayPricebook?.name || pricebook?.name;
                                    const pricebookId = displayPricebook?.id || pricebook?.id;

                                    console.log('Pricebook display debug:', {
                                        displayPricebook,
                                        pricebook,
                                        companyName,
                                        pricebookName,
                                        pricebookId,
                                    });

                                    const name = pricebookName || `Pricebook #${pricebookId}`;
                                    return companyName ? `${companyName} - ${name}` : name;
                                })()}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                            onClick={loadDocs}
                            title='Rule Ranking Documentation'
                            color='primary'
                        >
                            <HelpOutlineIcon />
                        </IconButton>
                        <Button
                            variant='contained'
                            color='primary'
                            startIcon={<AddIcon />}
                            onClick={() => setAddDialogOpen(true)}
                        >
                            Add Rule
                        </Button>
                    </Box>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Service Type</TableCell>
                                <TableCell>Repair Type</TableCell>
                                <TableCell>Part</TableCell>
                                <TableCell>Position</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Rank</TableCell>
                                <TableCell>Year Range</TableCell>
                                <TableCell>Make</TableCell>
                                <TableCell>Model</TableCell>
                                <TableCell align='right'>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={10} align='center'>
                                        Loading rules...
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && sortedAndGroupedRules.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={10} align='center'>
                                        No rules found. Click 'Add Rule' to create one.
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && sortedAndGroupedRules.map((rule, index) => {

                                const serviceInfo = rule._serviceInfo || getServiceDetailInfo(rule.service_detail_id);

                                // Check if this is the start of a new group
                                const prevRule = index > 0 ? sortedAndGroupedRules[index - 1] : null;
                                const prevServiceInfo = prevRule?._serviceInfo;
                                const isNewGroup = !prevRule ||
                                    prevServiceInfo?.repair_type !== serviceInfo.repair_type ||
                                    prevServiceInfo?.part !== serviceInfo.part ||
                                    prevServiceInfo?.position !== serviceInfo.position;

                                return (
                                    <React.Fragment key={rule.id}>
                                        {isNewGroup && index > 0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={10}
                                                    sx={{
                                                        height: '8px',
                                                        padding: 0,
                                                        backgroundColor: 'action.hover',
                                                    }}
                                                />
                                            </TableRow>
                                        )}
                                        <TableRow>
                                            <TableCell>{serviceInfo.service_type}</TableCell>
                                            <TableCell>
                                                <Typography
                                                    component='span'
                                                    sx={{
                                                        fontWeight: isNewGroup ? 600 : 400,
                                                    }}
                                                >
                                                    {serviceInfo.repair_type}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    component='span'
                                                    sx={{
                                                        fontWeight: isNewGroup ? 600 : 400,
                                                    }}
                                                >
                                                    {serviceInfo.part}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    component='span'
                                                    sx={{
                                                        fontWeight: isNewGroup ? 600 : 400,
                                                    }}
                                                >
                                                    {serviceInfo.position}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {rule.price != null ? `$${rule.price.toFixed(2)}` : (
                                                    <Typography component='span' sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                                        Quote Required
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{rule.rank || '-'}</TableCell>
                                            <TableCell>{formatYearRange(rule.year_range)}</TableCell>
                                            <TableCell>{formatArray(rule.make, 'All Makes')}</TableCell>
                                            <TableCell>{formatArray(rule.model, 'All Models')}</TableCell>
                                            <TableCell align='right'>
                                                <IconButton
                                                    color='error'
                                                    size='small'
                                                    onClick={() => handleDeleteRule(rule.id)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Add Rule Dialog */}
                <Dialog
                    open={addDialogOpen}
                    onClose={() => setAddDialogOpen(false)}
                    maxWidth='md'
                    fullWidth
                    PaperProps={{ elevation: 8 }}
                >
                    <DialogTitle>Add New Rule</DialogTitle>
                    <DialogContent>
                        {(() => {
                            const startYear = newRule.year_start ? parseInt(newRule.year_start) : undefined;
                            const endYear = newRule.open_ended ? 9999 : (newRule.year_end ? parseInt(newRule.year_end) : undefined);
                            const isYearRangeInvalid = Boolean(startYear && endYear && endYear < 9999 && endYear < startYear);
                            return isYearRangeInvalid ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant='body2' color='warning.main'>
                                        End year cannot be less than start year.
                                    </Typography>
                                </Box>
                            ) : null;
                        })()}
                        <Grid container rowSpacing={0.75} columnSpacing={1.5} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    value={newRule.service_type}
                                    onChange={(event, newValue) => {
                                        /* eslint-disable-next-line camelcase */
                                        setNewRule({ ...newRule, service_type: newValue, repair_type: '', part: '' });
                                    }}
                                    options={serviceTypeOptions}
                                    disabled
                                    renderInput={(params) => (
                                        <TextField {...params} label='Service Type *' />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    value={newRule.repair_type}
                                    onChange={(event, newValue) => {
                                        /* eslint-disable-next-line camelcase */
                                        setNewRule({ ...newRule, repair_type: newValue, part: '' });
                                    }}
                                    options={repairTypeOptions}
                                    renderInput={(params) => (
                                        <TextField {...params} label='Repair Type *' />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    value={newRule.part}
                                    onChange={(event, newValue) => {
                                        setNewRule({ ...newRule, part: newValue });
                                    }}
                                    options={partOptions}
                                    renderInput={(params) => (
                                        <TextField {...params} label='Part *' />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    value={newRule.position}
                                    onChange={(event, newValue) => {
                                        setNewRule({ ...newRule, position: newValue });
                                    }}
                                    options={positionOptions}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            label='Position'
                                            helperText='Leave blank if this rule applies regardless of position'
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label='Price'
                                    type='number'
                                    value={newRule.price}
                                    onChange={(e) => setNewRule({ ...newRule, price: e.target.value })}
                                    placeholder='0.00'
                                    disabled={requiresQuote}
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                                    }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={requiresQuote}
                                            onChange={(e) => {
                                                setRequiresQuote(e.target.checked);
                                                if (e.target.checked) {
                                                    setNewRule({ ...newRule, price: '' });
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant='body2'>
                                            Manual Quote Required
                                        </Typography>
                                    }
                                    sx={{ mt: 0.5 }}
                                />
                            </Grid>
                            <Grid item xs={6} md={6} sx={{ pt: '0 !important' }}>
                                <TextField
                                    fullWidth
                                    label='Year Start'
                                    type='number'
                                    margin='dense'
                                    value={newRule.year_start}
                                    onChange={(e) => {
                                        /* eslint-disable-next-line camelcase */
                                        setNewRule({ ...newRule, year_start: e.target.value });
                                    }}
                                    placeholder='Optional'
                                    helperText={newRule.open_ended ? 'Leave blank for all years; future years included' : 'Leave blank for all years'}
                                />
                            </Grid>
                            <Grid item xs={6} md={6} sx={{ pt: '0 !important' }}>
                                {(() => {
                                    const startYear = newRule.year_start ? parseInt(newRule.year_start) : undefined;
                                    const endYear = newRule.open_ended ? 9999 : (newRule.year_end ? parseInt(newRule.year_end) : undefined);
                                    const isYearRangeInvalid = Boolean(startYear && endYear && endYear < 9999 && endYear < startYear);
                                    return (
                                        <TextField
                                            fullWidth
                                            label='Year End'
                                            type='number'
                                            margin='dense'
                                            value={newRule.open_ended ? '9999' : newRule.year_end}
                                            onChange={(e) => {
                                                /* eslint-disable-next-line camelcase */
                                                setNewRule({ ...newRule, year_end: e.target.value });
                                            }}
                                            placeholder='Optional'
                                            helperText={isYearRangeInvalid
                                                ? 'End year cannot be less than start year'
                                                : (newRule.open_ended ? 'Includes future years' : 'Set end year or toggle Include Future Years')}
                                            disabled={newRule.open_ended}
                                            error={isYearRangeInvalid}
                                        />
                                    );
                                })()}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newRule.open_ended}
                                            onChange={(e) => {
                                                // eslint-disable-next-line camelcase
                                                setNewRule({ ...newRule, open_ended: e.target.checked });
                                            }}
                                        />
                                    }
                                    label={<Typography variant='body2'>Include Future Years</Typography>}
                                    sx={{ mt: 0.75 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={vehicleMakes}
                                    value={newRule.make}
                                    onChange={(e, newValue) => setNewRule({ ...newRule, make: newValue })}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label='Make'
                                            placeholder='Leave blank for all makes'
                                            helperText='Leave blank to apply to all makes. Can select multiple.'
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={vehicleModels}
                                    value={newRule.model}
                                    onChange={(e, newValue) => setNewRule({ ...newRule, make: newRule.make, model: newValue })}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label='Model'
                                            placeholder='Leave blank for all models'
                                            helperText='Leave blank to apply to all models. Can select multiple.'
                                        />
                                    )}
                                />
                                {(() => {
                                    const isDisabled = !newRule.make?.length || newRule.model?.length > 0;
                                    return (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={newRule.includes_future_models}
                                                    onChange={(e) => {
                                                        // eslint-disable-next-line camelcase
                                                        setNewRule({ ...newRule, includes_future_models: e.target.checked });
                                                    }}
                                                    disabled={isDisabled}
                                                    sx={{
                                                        '&.Mui-disabled': {
                                                            color: 'rgba(0, 0, 0, 0.26)',
                                                            '& .MuiSvgIcon-root': {
                                                                color: 'rgba(0, 0, 0, 0.26)',
                                                            },
                                                        },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    flexDirection: 'column',
                                                    opacity: isDisabled ? 0.5 : 1,
                                                }}>
                                                    <Typography variant='body2' color={isDisabled ? 'text.disabled' : 'text.primary'}>
                                                        Include Future Models
                                                    </Typography>
                                                    <Typography variant='caption' color='text.secondary' sx={{ mt: 0.25, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        When checked, this rule will automatically apply to new models added to the database in the future.
                                                        Only available when a make is selected but no specific models are chosen.
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{ 
                                                mt: 3, 
                                                alignItems: 'flex-start',
                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            }}
                                        />
                                    );
                                })()}
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                        {(() => {
                            const startYear = newRule.year_start ? parseInt(newRule.year_start) : undefined;
                            const endYear = newRule.open_ended ? 9999 : (newRule.year_end ? parseInt(newRule.year_end) : undefined);
                            const isYearRangeInvalid = Boolean(startYear && endYear && endYear < 9999 && endYear < startYear);
                            return (
                                <Button onClick={() => handleAddRule()} variant='contained' color='primary' disabled={isYearRangeInvalid}>
                                    Add Rule
                                </Button>
                            );
                        })()}
                    </DialogActions>
                </Dialog>

                {/* Conflict Resolution Dialog */}
                <Dialog
                    open={conflictDialogOpen}
                    onClose={() => setConflictDialogOpen(false)}
                    maxWidth='md'
                    fullWidth
                    PaperProps={{ elevation: 8 }}
                >
                    <DialogTitle>Rule Conflict Detected</DialogTitle>
                    <DialogContent>
                        <Typography variant='body1' sx={{ mb: 2 }}>
                            {conflictData?.message || 'The rule you are trying to add conflicts with existing rules.'}
                        </Typography>

                        {conflictData?.payload?.rulesToUpdate && conflictData.payload.rulesToUpdate.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Rules that will be updated:
                                </Typography>
                                {conflictData.payload.rulesToUpdate.map((item, idx) => {
                                    const rule = item.rule;
                                    const updates = item.fieldsToUpdate;
                                    const serviceInfo = getServiceDetailInfo(rule.service_detail_id);

                                    return (
                                        <Card key={idx} variant='outlined' sx={{ mb: 2, p: 2 }}>
                                            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                                                {rule.description || `Rule #${rule.id}`}
                                            </Typography>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}>
                                                    <Typography variant='caption' color='text.secondary'>Service:</Typography>
                                                    <Typography variant='body2'>{serviceInfo.service_type}</Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant='caption' color='text.secondary'>Repair Type:</Typography>
                                                    <Typography variant='body2'>{serviceInfo.repair_type}</Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant='caption' color='text.secondary'>Part:</Typography>
                                                    <Typography variant='body2'>{serviceInfo.part}</Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant='caption' color='text.secondary'>Position:</Typography>
                                                    <Typography variant='body2'>{serviceInfo.position}</Typography>
                                                </Grid>
                                                {updates.price !== undefined && (
                                                    <Grid item xs={12}>
                                                        <Typography variant='caption' color='text.secondary'>Price Change:</Typography>
                                                        <Typography variant='body2'>
                                                            <span style={{ textDecoration: 'line-through', color: '#888' }}>
                                                                {rule.price != null ? `$${rule.price.toFixed(2)}` : 'Quote Required'}
                                                            </span>
                                                            {' → '}
                                                            <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                                                                {updates.price != null ? `$${updates.price.toFixed(2)}` : 'Quote Required'}
                                                            </span>
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Card>
                                    );
                                })}
                            </Box>
                        )}

                        <Typography variant='body2' color='warning.main' sx={{ mt: 2 }}>
                            Click 'Force Update' to proceed with these changes, or 'Cancel' to abort.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConflictDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleForceAddRule} variant='contained' color='warning'>
                            Force Update
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Documentation Modal */}
                <Dialog
                    open={docsOpen}
                    onClose={() => setDocsOpen(false)}
                    maxWidth='md'
                    fullWidth
                >
                    <DialogTitle>Pricebook Rule Ranking Guide</DialogTitle>
                    <DialogContent>
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                table: ({ children }) => (
                                    <Table sx={{ mb: 2, border: 1, borderColor: 'divider' }}>
                                        {children}
                                    </Table>
                                ),
                                thead: ({ children }) => (
                                    <TableHead>
                                        {children}
                                    </TableHead>
                                ),
                                tbody: ({ children }) => (
                                    <TableBody>
                                        {children}
                                    </TableBody>
                                ),
                                tr: ({ children }) => (
                                    <TableRow>
                                        {children}
                                    </TableRow>
                                ),
                                th: ({ children }) => (
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'action.hover' }}>
                                        {children}
                                    </TableCell>
                                ),
                                td: ({ children }) => (
                                    <TableCell>
                                        {children}
                                    </TableCell>
                                ),
                            }}
                        >
                            {docsContent}
                        </ReactMarkdown>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDocsOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
};

RulesTable.propTypes = {
    pricebook: PropTypes.object,
    displayPricebook: PropTypes.object,
    errorContext: PropTypes.object.isRequired,
    onRulesUpdated: PropTypes.func,
};

export default RulesTable;
